// CustIdSelector.js - Customer selection modal component
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
  Box,
  Divider,
  Alert,
} from "@mui/material";
import { Person, CheckCircle } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

export default function CustIdSelector({ open, onClose, onSelect, customerData }) {
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [selectedCustomerData, setSelectedCustomerData] = useState(null);
  const navigate = useNavigate();


  const handleCustomerChange = (customer) => {
    console.log("🔍 [CustIdSelector] Customer selected:", customer);
    setSelectedCustomerId(customer.customer_id);
    setSelectedCustomerData(customer);
  };

  const handleConfirm = () => {
    if (!selectedCustomerId || !selectedCustomerData) {
      alert("Please select a customer to continue");
      return;
    }

    console.log("✅ [CustIdSelector] Confirming selection:", selectedCustomerId);
    
    // Store in localStorage
    localStorage.setItem("cust_id_selected", selectedCustomerId);
    localStorage.setItem("selected_customer_data", JSON.stringify(selectedCustomerData));
    
    // Call parent callback
    onSelect(selectedCustomerId, selectedCustomerData);
  };

  const handleCancel = () => {
    // Reset selection
    setSelectedCustomerId("");
    setSelectedCustomerData(null);
    
    // Call parent close handler
    onClose();
     navigate("/", { replace: true });
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing by clicking outside or pressing ESC
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleCancel();
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: "300px"
        }
      }}
    >
      <DialogTitle sx={{ bgcolor: "#1976d2",
         color: "white",
          py: 2 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Person />
          <Typography variant="h6" component="div">
            Select Customer Account
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {!customerData || customerData.length === 0 ? (
          <Alert severity="warning">
            No customer accounts found
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2,mt:2 }}>
              {customerData.length > 1 
                ? "Multiple accounts found. Please select one to continue:" 
                : "Please confirm your account:"}
            </Typography>

            <RadioGroup 
              value={selectedCustomerId} 
              onChange={(e) => {
                const customer = customerData.find(c => c.customer_id === e.target.value);
                if (customer) handleCustomerChange(customer);
              }}
            >
              <List sx={{ bgcolor: "background.paper", borderRadius: 1 }}>
                {customerData.map((customer, index) => (
                  <React.Fragment key={customer.customer_id}>
                    <ListItem
                      disablePadding
                      sx={{
                        bgcolor: selectedCustomerId === customer.customer_id 
                          ? "action.selected" 
                          : "transparent",
                        borderRadius: 1,
                        mb: 1,
                        border: selectedCustomerId === customer.customer_id
                          ? "2px solid"
                          : "1px solid",
                        borderColor: selectedCustomerId === customer.customer_id
                          ? "primary.main"
                          : "divider",
                        transition: "all 0.2s ease"
                      }}
                    >
                      <ListItemButton
                        onClick={() => handleCustomerChange(customer)}
                        sx={{ py: 2 }}
                      >
                        <FormControlLabel
                          value={customer.customer_id}
                          control={<Radio />}
                          label={
                            <Box sx={{ ml: 1 }}>
                              <Typography 
                                variant="subtitle1" 
                                fontWeight="bold"
                                color="text.primary"
                              >
                                {customer.customer_name}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                              >
                                Customer ID: <strong>{customer.customer_id}</strong>
                              </Typography>
                              {customer.customer_phone && (
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary"
                                  sx={{ display: "block", mt: 0.5 }}
                                >
                                  📞 {customer.customer_phone}
                                </Typography>
                              )}
                             
                            </Box>
                          }
                          sx={{ width: "100%", m: 0 }}
                        />
                      </ListItemButton>
                    </ListItem>
                    
                    {index < customerData.length - 1 && (
                      <Divider sx={{ my: 1 }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
            </RadioGroup>

            {selectedCustomerId && (
              <Alert 
                severity="success" 
                icon={<CheckCircle />}
                sx={{ mt: 2 }}
              >
                Selected: <strong>{selectedCustomerData?.customer_name}</strong> (ID: {selectedCustomerId})
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 4, py: 4, bgcolor: "grey.50",display: "flex",
    justifyContent: "center",gap: 0 }}>
        
        <Button
          onClick={handleCancel}
          variant="outlined"
          color="error"
          sx={{ minWidth: 120 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={!selectedCustomerId}
          startIcon={<CheckCircle />}
          sx={{ minWidth: 120 }}
        >
          Continue
        </Button>
        
      </DialogActions>
    
    </Dialog>
  );
}
